// ─────────────────────────────────────────────────────────────────────────────
// Snakes and Ladders board — 10×10 boustrophedon grid, square 1 at bottom-left,
// square 100 at top-left. Geometry is the single source of truth for the engine
// and the renderer.
// ─────────────────────────────────────────────────────────────────────────────

export type Color = 'red' | 'green' | 'yellow' | 'blue';
export const COLORS: Color[] = ['red', 'green', 'yellow', 'blue'];
export const COLOR_LABELS: Record<Color, string> = {
  red: 'Red',
  green: 'Green',
  yellow: 'Yellow',
  blue: 'Blue',
};

export const BOARD_SIZE = 10;
export const LAST_SQUARE = 100;

export interface Cell {
  x: number;
  y: number;
}

/** Classic Milton Bradley / Indian board ladders: foot → top. */
export const LADDERS: Record<number, number> = {
  1: 38,
  4: 14,
  9: 31,
  21: 42,
  28: 84,
  36: 44,
  51: 67,
  71: 91,
  80: 100,
};

/** Classic snakes: head → tail. */
export const SNAKES: Record<number, number> = {
  16: 6,
  47: 26,
  49: 11,
  56: 53,
  62: 19,
  64: 60,
  87: 24,
  93: 73,
  95: 75,
  98: 78,
};

export interface SnakeStyle {
  from: number;
  to: number;
  fill: string;
  dark: string;
  light: string;
  /** Signed bulge in grid units — fans snakes so they don't pile up. */
  bulge: number;
}

export const SNAKE_STYLES: SnakeStyle[] = [
  { from: 16, to: 6, fill: '#2f9e44', dark: '#146c2e', light: '#8ce99a', bulge: 1.15 },
  { from: 47, to: 26, fill: '#e03131', dark: '#9b1c1c', light: '#ffa8a8', bulge: -1.4 },
  { from: 49, to: 11, fill: '#7048e8', dark: '#5f3dc4', light: '#d0bfff', bulge: 1.6 },
  { from: 56, to: 53, fill: '#f08c00', dark: '#e67700', light: '#ffd43b', bulge: 0.85 },
  { from: 62, to: 19, fill: '#1971c2', dark: '#1864ab', light: '#74c0fc', bulge: -1.7 },
  { from: 64, to: 60, fill: '#0ca678', dark: '#087f5b', light: '#63e6be', bulge: 0.7 },
  { from: 87, to: 24, fill: '#c2255c', dark: '#a61e4d', light: '#faa2c1', bulge: 1.9 },
  { from: 93, to: 73, fill: '#5c940d', dark: '#2b8a3e', light: '#c0eb75', bulge: -1.1 },
  { from: 95, to: 75, fill: '#d9480f', dark: '#c2410c', light: '#ffc078', bulge: 1.25 },
  { from: 98, to: 78, fill: '#364fc7', dark: '#3b5bdb', light: '#91a7ff', bulge: -1.35 },
];

export const LADDER_LIST: { from: number; to: number }[] = Object.entries(LADDERS).map(([from, to]) => ({
  from: Number(from),
  to,
}));

/**
 * Grid cell for a square. x = column 0..9 left→right, y = row 0..9 top→bottom
 * (SVG space). Square 1 is bottom-left; even rows-from-bottom go left→right.
 */
export function squareToCell(n: number): Cell {
  if (n < 1 || n > LAST_SQUARE) return { x: -1, y: 10 };
  const rowFromBottom = Math.floor((n - 1) / BOARD_SIZE);
  const posInRow = (n - 1) % BOARD_SIZE;
  const y = BOARD_SIZE - 1 - rowFromBottom;
  const x = rowFromBottom % 2 === 0 ? posInRow : BOARD_SIZE - 1 - posInRow;
  return { x, y };
}

/** Squares visited while hopping from `from` (exclusive) to `to` (inclusive). */
export function hopSquares(from: number, to: number): number[] {
  if (to <= from) return [];
  const out: number[] = [];
  for (let n = from + 1; n <= to; n++) out.push(n);
  return out;
}

export function applyTeleport(square: number): { to: number; kind: 'snake' | 'ladder' | null } {
  if (LADDERS[square]) return { to: LADDERS[square], kind: 'ladder' };
  if (SNAKES[square]) return { to: SNAKES[square], kind: 'snake' };
  return { to: square, kind: null };
}

/** Cubic control points in grid space for a snake body from head → tail. */
export function snakeControls(style: SnakeStyle): { c1: Cell; c2: Cell } {
  const a = squareToCell(style.from);
  const b = squareToCell(style.to);
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  const px = (-dy / len) * style.bulge;
  const py = (dx / len) * style.bulge;
  return {
    c1: { x: a.x + dx * 0.28 + px, y: a.y + dy * 0.28 + py },
    c2: { x: a.x + dx * 0.72 + px, y: a.y + dy * 0.72 + py },
  };
}

export function sampleCubic(p0: Cell, c1: Cell, c2: Cell, p3: Cell, steps: number): Cell[] {
  const out: Cell[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const u = 1 - t;
    out.push({
      x: u * u * u * p0.x + 3 * u * u * t * c1.x + 3 * u * t * t * c2.x + t * t * t * p3.x,
      y: u * u * u * p0.y + 3 * u * u * t * c1.y + 3 * u * t * t * c2.y + t * t * t * p3.y,
    });
  }
  return out;
}
